/**
 * Messaging - Interface de messagerie interne pour vendeurs
 * Permet de voir et répondre aux messages clients
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { io, Socket } from 'socket.io-client';
import { logger } from '../utils/logger';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
// URL de base pour WebSocket (sans /api)
const WS_BASE_URL = API_URL.replace(/\/api$/, '');

interface Message {
  id: string;
  conversation_id: string;
  sender_type: 'CUSTOMER' | 'VENDOR' | 'SYSTEM';
  sender_id: string | null;
  sender_name: string | null;
  content: string;
  content_type: string;
  metadata?: Record<string, any>;
  read_at: string | null;
  created_at: string;
}

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

interface Conversation {
  id: string;
  tenant_id: string;
  customer_id: string;
  customer: Customer;
  subject?: string;
  context_type?: string;
  context_id?: string;
  status: 'OPEN' | 'PENDING' | 'RESOLVED' | 'CLOSED';
  assigned_vendor_id?: string;
  assigned_vendor?: { id: string; name: string };
  unread_customer: number;
  unread_vendor: number;
  last_message_at?: string;
  messages?: Message[];
  created_at: string;
}

export default function Messaging() {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'pending'>('all');
  const [unreadCount, setUnreadCount] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);

  // Charger les conversations
  const loadConversations = useCallback(async () => {
    if (!tenant) return;

    try {
      const params: Record<string, string> = {};
      if (filter === 'unread') params.has_unread = 'true';
      if (filter === 'pending') params.status = 'PENDING';

      const response = await api.get(`/messaging/conversations`, { params });
      // api.get() retourne déjà response.data, donc on accède directement à .data
      setConversations(response.data || []);
    } catch (err) {
      logger.error('Erreur chargement conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [tenant, filter]);

  // Charger le nombre de non lus
  const loadUnreadCount = useCallback(async () => {
    if (!tenant) return;
    try {
      const response = await api.get(`/messaging/conversations/unread-count`);
      // api.get() retourne déjà response.data
      setUnreadCount(response.unread_count || 0);
    } catch {
      // Ignore
    }
  }, [tenant]);

  // Charger une conversation spécifique
  const loadConversation = useCallback(async (conversationId: string) => {
    if (!tenant) return;

    try {
      const response = await api.get(`/messaging/conversations/${conversationId}`);
      // api.get() retourne déjà response.data
      setSelectedConversation(response);
      setMessages(response.messages || []);

      // Marquer comme lu
      await api.put(`/messaging/conversations/${conversationId}/read`);
      loadUnreadCount();
      loadConversations();
    } catch (err) {
      logger.error('Erreur chargement conversation:', err);
    }
  }, [tenant, loadUnreadCount, loadConversations]);

  // Effet initial
  useEffect(() => {
    if (tenant) {
      loadConversations();
      loadUnreadCount();
    }
  }, [tenant, filter, loadConversations, loadUnreadCount]);

  // Connexion WebSocket
  useEffect(() => {
    if (!tenant) return;

    const vendorData = localStorage.getItem('vendor');
    const vendor = vendorData ? JSON.parse(vendorData) : null;
    if (!vendor) return;

    const socket = io(`${WS_BASE_URL}/messaging`, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join_messaging', {
        tenant_id: tenant.id,
        user_type: 'vendor',
        user_id: vendor.id,
        user_name: vendor.name,
      });
    });

    // Nouveau message
    socket.on('new_message', (data: { message: Message; conversation_id: string }) => {
      if (selectedConversation?.id === data.conversation_id) {
        setMessages(prev => [...prev, data.message]);
      }
      loadConversations();
      loadUnreadCount();
    });

    // Notification de nouveau message
    socket.on('new_message_notification', () => {
      loadUnreadCount();
      loadConversations();
    });

    // Messages lus
    socket.on('messages_read', (data: { conversation_id: string; read_by: string }) => {
      if (data.read_by === 'customer' && selectedConversation?.id === data.conversation_id) {
        setMessages(prev =>
          prev.map(m =>
            m.sender_type === 'VENDOR' && !m.read_at
              ? { ...m, read_at: new Date().toISOString() }
              : m
          )
        );
      }
    });

    // Indicateur de frappe
    socket.on('user_typing', (data: { conversation_id: string; is_typing: boolean; user_name: string }) => {
      // Afficher l'indicateur (optionnel)
      logger.log(`${data.user_name} ${data.is_typing ? 'tape...' : 'a arrêté de taper'}`);
    });

    return () => {
      socket.disconnect();
    };
  }, [tenant, selectedConversation?.id, loadConversations, loadUnreadCount]);

  // Auto-scroll vers le bas des messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Rejoindre la conversation WebSocket
  useEffect(() => {
    if (socketRef.current && selectedConversation) {
      socketRef.current.emit('join_conversation', {
        conversation_id: selectedConversation.id,
      });

      return () => {
        socketRef.current?.emit('leave_conversation', {
          conversation_id: selectedConversation.id,
        });
      };
    }
  }, [selectedConversation?.id]);

  // Envoyer un message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !tenant) return;

    setSending(true);
    try {
      const response = await api.post(
        `/messaging/conversations/${selectedConversation.id}/messages`,
        { content: newMessage.trim() }
      );
      // api.post() retourne déjà response.data
      setMessages(prev => [...prev, response]);
      setNewMessage('');
    } catch (err) {
      logger.error('Erreur envoi message:', err);
      alert('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  // Actions sur conversation
  const handleResolve = async () => {
    if (!selectedConversation) return;
    try {
      await api.put(`/messaging/conversations/${selectedConversation.id}/resolve`);
      loadConversation(selectedConversation.id);
      loadConversations();
    } catch (err) {
      logger.error('Erreur:', err);
    }
  };

  const handleClose = async () => {
    if (!selectedConversation) return;
    try {
      await api.put(`/messaging/conversations/${selectedConversation.id}/close`);
      setSelectedConversation(null);
      loadConversations();
    } catch (err) {
      logger.error('Erreur:', err);
    }
  };

  const handleReopen = async () => {
    if (!selectedConversation) return;
    try {
      await api.put(`/messaging/conversations/${selectedConversation.id}/reopen`);
      loadConversation(selectedConversation.id);
      loadConversations();
    } catch (err) {
      logger.error('Erreur:', err);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) +
        ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'OPEN':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Ouvert</span>;
      case 'PENDING':
        return <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">En attente</span>;
      case 'RESOLVED':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Résolu</span>;
      case 'CLOSED':
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">Fermé</span>;
      default:
        return null;
    }
  };

  return (
    // Hauteur = écran moins la barre du tableau de bord et ses marges (la page
    // prenait tout l'écran EN PLUS de cette barre). Sur téléphone : la liste,
    // puis la conversation choisie, avec un retour (23/10/2026).
    <div className="h-[calc(100dvh-6rem)] sm:h-[calc(100dvh-7rem)] lg:h-[calc(100dvh-8rem)] flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                Messages
                {unreadCount > 0 && (
                  <span className="px-3 py-1 bg-red-500 text-white text-sm rounded-full">
                    {unreadCount}
                  </span>
                )}
              </h1>
              <p className="text-gray-500 text-sm">Messagerie clients</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Toutes
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Non lues
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'pending'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              En attente
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Liste des conversations */}
        <div className={`w-full sm:w-96 bg-white border-r overflow-y-auto ${selectedConversation ? 'hidden sm:block' : 'block'}`}>
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <div className="text-5xl mb-4">💬</div>
              <p>Aucune conversation</p>
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => loadConversation(conv.id)}
                className={`p-4 border-b cursor-pointer transition-colors ${
                  selectedConversation?.id === conv.id
                    ? 'bg-blue-50 border-l-4 border-l-blue-600'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 truncate">
                        {conv.customer.first_name} {conv.customer.last_name}
                      </span>
                      {conv.unread_vendor > 0 && (
                        <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                          {conv.unread_vendor}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {conv.customer.email}
                    </p>
                    {conv.messages?.[0] && (
                      <p className="text-sm text-gray-600 mt-1 truncate">
                        {conv.messages[0].sender_type === 'VENDOR' && '→ '}
                        {conv.messages[0].content}
                      </p>
                    )}
                  </div>
                  <div className="text-right ml-2">
                    <div className="text-xs text-gray-400">
                      {conv.last_message_at ? formatDate(conv.last_message_at) : ''}
                    </div>
                    <div className="mt-1">
                      {getStatusBadge(conv.status)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Zone de conversation */}
        <div className={`flex-1 min-w-0 flex-col ${selectedConversation ? 'flex' : 'hidden sm:flex'}`}>
          {selectedConversation ? (
            <>
              {/* Header conversation */}
              <div className="bg-white border-b px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-2">
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="sm:hidden -ml-1 px-2 min-h-[44px] text-blue-700 font-medium"
                  aria-label="Retour aux conversations"
                >
                  ← Conversations
                </button>
                <div className="min-w-0">
                  <h2 className="font-bold text-lg">
                    {selectedConversation.customer.first_name} {selectedConversation.customer.last_name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                    <span className="break-all">{selectedConversation.customer.email}</span>
                    {selectedConversation.customer.phone && (
                      <span>{selectedConversation.customer.phone}</span>
                    )}
                    {getStatusBadge(selectedConversation.status)}
                  </div>
                </div>

                <div className="flex gap-2">
                  {selectedConversation.status === 'OPEN' || selectedConversation.status === 'PENDING' ? (
                    <>
                      <button
                        onClick={handleResolve}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        Marquer résolu
                      </button>
                      <button
                        onClick={handleClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        Fermer
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleReopen}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Réouvrir
                    </button>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === 'VENDOR' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                        msg.sender_type === 'VENDOR'
                          ? 'bg-blue-600 text-white rounded-br-sm'
                          : msg.sender_type === 'SYSTEM'
                          ? 'bg-gray-200 text-gray-600 text-sm italic'
                          : 'bg-white border rounded-bl-sm'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <div
                        className={`text-xs mt-1 flex items-center gap-2 ${
                          msg.sender_type === 'VENDOR' ? 'text-blue-200' : 'text-gray-400'
                        }`}
                      >
                        <span>{formatDate(msg.created_at)}</span>
                        {msg.sender_type === 'VENDOR' && msg.read_at && (
                          <span>✓ Lu</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Zone de saisie */}
              {(selectedConversation.status === 'OPEN' || selectedConversation.status === 'PENDING') && (
                <div className="bg-white border-t p-4">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                      placeholder="Votre message..."
                      className="flex-1 px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-blue-500"
                      disabled={sending}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium"
                    >
                      {sending ? '...' : 'Envoyer'}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <div className="text-6xl mb-4">💬</div>
                <p className="text-xl">Sélectionnez une conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
